import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import TableManager from './components/TableManager';
import SchemaDetailsPage from './pages/SchemaDetailsPage';
import RelationshipsPage from './pages/RelationshipsPage';
import FeatureExplorer from './components/FeatureExplorer';
import ApiExplorer from './components/ApiExplorer';
import ApiTester from './components/ApiTester';
import APIDocumentation from './components/APIDocumentation';
import { getToken } from './utils/auth';
import { jwtDecode } from 'jwt-decode';
import RelationshipExplorer from './components/RelationshipExplorer';
import SecurityDashboard from './components/SecurityDashboard';

// Main Layout Component
function MainLayout() {
    const [activeView, setActiveView] = useState('tables');
    const [userRole, setUserRole] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        // Decode token to get user role
        const token = getToken();
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUserRole(decoded.role);
            } catch (e) {
                console.error('Error decoding token:', e);
            }
        }
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    return (
        <div className="container mx-auto">
            <div className="bg-white shadow">
                <div className="px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-6">
                        <h1 className="text-3xl font-bold">Dentulu Internal Platform</h1>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-600">
                                Logged in as: <span className="font-medium">{userRole}</span>
                            </span>
                            <button 
                                onClick={handleLogout}
                                className="bg-red-600 text-white px-4 py-2 rounded"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                    
                    {/* Navigation Tabs */}
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8 overflow-x-auto">
                            <button
                                onClick={() => setActiveView('tables')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                                    activeView === 'tables'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Database Tables
                            </button>
                            <button
                                onClick={() => setActiveView('overview')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                                    activeView === 'overview'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Overview
                            </button>
                            <button
                                onClick={() => setActiveView('features')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                                    activeView === 'features'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Features
                            </button>
                            <button
                                onClick={() => setActiveView('relationships')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                                    activeView === 'relationships'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Relationships & Events
                            </button>
                            <button
                                onClick={() => setActiveView('api-explorer')}
                                className={`py-2 px-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                                    activeView === 'api-explorer'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                API Explorer
                            </button>
                            <button
                                onClick={() => setActiveView('api-tester')}
                                className={`py-2 px-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                                    activeView === 'api-tester'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                API Tester
                            </button>
                            <button
                                onClick={() => setActiveView('security')}
                                className={`py-2 px-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                                    activeView === 'security'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Security Dashboard
                            </button>
                            <button
                                onClick={() => setActiveView('api-docs')}
                                className={`py-2 px-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                                    activeView === 'api-docs'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                📚 API Documentation
                            </button>
                            {/* Only show Admin tab for admin users */}
                            {userRole === 'admin' && (
                                <button
                                    onClick={() => setActiveView('admin')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                                        activeView === 'admin'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Admin
                                </button>
                            )}
                        </nav>
                    </div>
                </div>
            </div>

            <div className="py-8">
                {activeView === 'tables' && <TableManager />}
                {activeView === 'overview' && <HomePage />}
                {activeView === 'features' && <FeatureExplorer />}
                {activeView === 'admin' && userRole === 'admin' && <AdminPage />}
                {activeView === 'relationships' && <RelationshipExplorer />}
                {activeView === 'api-explorer' && <ApiExplorer />}
                {activeView === 'api-tester' && <ApiTester userRole={userRole} />}
                {activeView === 'api-docs' && <APIDocumentation />}
                {activeView === 'security' && <SecurityDashboard />}
                
            </div>
        </div>
    );
}

// Main App Component
export default function App() {
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const existingToken = getToken();
        setToken(existingToken);
        setLoading(false);
    }, []);

    const handleLogin = () => {
        const newToken = getToken();
        setToken(newToken);
    };

    if (loading) {
        return (
            <div className="container mx-auto p-8">
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <Router>
            <Routes>
                <Route path="/login" element={!token ? <LoginPage onLogin={handleLogin} /> : <Navigate to="/" />} />
                <Route path="/" element={token ? <MainLayout /> : <Navigate to="/login" />} />
                <Route path="/schema-details" element={token ? <SchemaDetailsPage /> : <Navigate to="/login" />} />
                <Route path="/relationships" element={token ? <RelationshipsPage /> : <Navigate to="/login" />} />
                <Route path="/docs-details" element={token ? <div className="p-8">Documentation Details Page (to be implemented)</div> : <Navigate to="/login" />} />
                <Route path="/security" element={<SecurityDashboard />} />
            </Routes>
        </Router>
    );
}